<script lang="ts" setup>
import { computed, reactive, Ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { AxiosResponse } from 'axios';

import { useVuelidate, Validation, ValidationArgs } from '@vuelidate/core';
import { required, maxLength } from '@/utils/validators';

import { useUiStore } from '@/stores';
import { goBack } from '@/composables/useNavigation';
import { IntegrationService } from '@/services';
import { toast, toastServiceError } from '@/services/core/notification';

import {
    FormAction,
    DefaultServiceResult,
    IntegrationServiceCreateResult,
    IntegrationType,
    MoskitIntegrationModel,
    MoskitIntegrationFormState,
    Permission,
} from '@/types';
import UiPanel from '@/components/UiPanel.vue';

const uiStore = useUiStore();
const route = useRoute();

const props = defineProps({
    action: {
        type: String,
        default: FormAction.CREATE,
    },
});

const formState = reactive<MoskitIntegrationFormState>({
    type: IntegrationType.Moskit,
    token: null,
    activityIds: {
        BUSY: null,
        DITCHED: null,
        ANSWERED: null,
        NO_ANSWER: null,
    },
    customFields: {
        value: null,
        fieldId: null,
        fieldType: null,
    },
    isActive: true,
});

const rules = computed<ValidationArgs<MoskitIntegrationFormState>>(() => ({
    type: { required },
    token: { required, maxLength: maxLength(250) },
    activityIds: {
        BUSY: { required },
        DITCHED: { required },
        ANSWERED: { required },
        NO_ANSWER: { required },
    },
    customFields: {
        value: {},
        fieldId: {},
        fieldType: {},
    },
    isActive: { required },
}));

const v$: Ref<Validation<ValidationArgs<MoskitIntegrationFormState>>> = useVuelidate(
    rules,
    formState,
);

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const getIntegration = async (id: number) => {
    await setLoading(true);
    try {
        const { data } = await IntegrationService.get<MoskitIntegrationModel>(id);
        // Map the API response to the form state
        formState.token = data.token;
        formState.activityIds.BUSY = data.activityIds.BUSY;
        formState.activityIds.DITCHED = data.activityIds.DITCHED;
        formState.activityIds.ANSWERED = data.activityIds.ANSWERED;
        formState.activityIds.NO_ANSWER = data.activityIds.NO_ANSWER;
        formState.customFields.value = data.customFields.value;
        formState.customFields.fieldId = data.customFields.fieldId;
        formState.customFields.fieldType = data.customFields.fieldType;
        formState.isActive = data.isActive;
    } catch (error) {
        toastServiceError(error);
    } finally {
        await setLoading(false);
    }
};

const onSubmit = async () => {
    const isValid = await v$.value.$validate();
    if (!isValid) return;

    await setLoading(true);

    try {
        let response: AxiosResponse<DefaultServiceResult | IntegrationServiceCreateResult>;

        if (props.action === FormAction.EDIT) {
            const id = Number(route.params.id);
            response = await IntegrationService.update<DefaultServiceResult>(id, formState);
        } else {
            response = await IntegrationService.create<IntegrationServiceCreateResult>(formState);
        }

        toast.success(response.data.message);
        v$.value.$reset();
        goBack({ name: 'ListIntegration' });
    } catch (error) {
        toastServiceError(error);
    } finally {
        await setLoading(false);
    }
};

onMounted(async () => {
    if (props.action === FormAction.EDIT) {
        await getIntegration(Number(route.params.id));
    }
});
</script>

<template>
    <div class="space-y-4 p-4 sm:p-6 lg:p-8">
        <UiPageHeader>
            <template #info>
                <h1 class="text-xl font-semibold text-gray-800">
                    {{ $t('core.entity.Integrations') }} -
                    {{ $t('integration.integrations.moskit.title') }}
                </h1>
            </template>
        </UiPageHeader>
        <form class="space-y-4" autocomplete="off" @submit.prevent="onSubmit">
            <UiPanel>
                <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    <UiTextInput
                        v-model="v$.token.$model"
                        name="token"
                        type="text"
                        :label="$t('integration.integrations.moskit.form.token')"
                        :errors="v$.token.$errors"
                        @blur="v$.token.$touch"
                    />
                </div>
                <div class="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-4">
                    <UiTextInput
                        v-model="v$.activityIds.BUSY.$model"
                        name="activityIdsBusy"
                        :label="$t('integration.integrations.moskit.form.activityIds.busy')"
                        :errors="v$.activityIds.BUSY.$errors"
                        @blur="v$.activityIds.BUSY.$touch"
                    />
                    <UiTextInput
                        v-model="v$.activityIds.DITCHED.$model"
                        name="activityIdsDitched"
                        :label="$t('integration.integrations.moskit.form.activityIds.ditched')"
                        :errors="v$.activityIds.DITCHED.$errors"
                        @blur="v$.activityIds.DITCHED.$touch"
                    />
                    <UiTextInput
                        v-model="v$.activityIds.ANSWERED.$model"
                        name="activityIdsAnswered"
                        :label="$t('integration.integrations.moskit.form.activityIds.answered')"
                        :errors="v$.activityIds.ANSWERED.$errors"
                        @blur="v$.activityIds.ANSWERED.$touch"
                    />
                    <UiTextInput
                        v-model="v$.activityIds.NO_ANSWER.$model"
                        name="activityIdsNoAnswer"
                        :label="$t('integration.integrations.moskit.form.activityIds.noAnswer')"
                        :errors="v$.activityIds.NO_ANSWER.$errors"
                        @blur="v$.activityIds.NO_ANSWER.$touch"
                    />
                </div>
            </UiPanel>
            <UiPanel>
                <h2 class="mb-2 text-lg font-semibold text-gray-800">
                    {{ $t('integration.integrations.moskit.form.customFields.title') }}
                </h2>
                <div class="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-4">
                    <UiTextInput
                        v-model="v$.customFields.value.$model"
                        name="customFieldsValue"
                        :label="$t('integration.integrations.moskit.form.customFields.value')"
                        :errors="v$.customFields.value.$errors"
                        @blur="v$.customFields.value.$touch"
                    />
                    <UiTextInput
                        v-model="v$.customFields.fieldId.$model"
                        name="customFieldsFieldId"
                        :label="$t('integration.integrations.moskit.form.customFields.fieldId')"
                        :errors="v$.customFields.fieldId.$errors"
                        @blur="v$.customFields.fieldId.$touch"
                    />
                    <UiTextInput
                        v-model="v$.customFields.fieldType.$model"
                        name="customFieldsFieldType"
                        :label="$t('integration.integrations.moskit.form.customFields.fieldType')"
                        :errors="v$.customFields.fieldType.$errors"
                        @blur="v$.customFields.fieldType.$touch"
                    />
                </div>
                <div class="mt-6 w-full">
                    <UiCheckboxInput
                        v-model="v$.isActive.$model"
                        name="isActive"
                        :label="$t('integration.integrations.moskit.form.isActive')"
                    />
                </div>
            </UiPanel>

            <div class="flex justify-end px-4 sm:px-0">
                <UiBackButton :fallbackTo="{ name: 'ListIntegration' }" />
                <UiButton
                    v-if="$can(Permission.EDIT_INTEGRATION)"
                    type="submit"
                    variant="primary"
                    text-variant="white"
                    class="ml-4"
                >
                    {{ $t('core.actions.Save') }}
                </UiButton>
            </div>
        </form>
        <UiCheckDirtyBeforeRouteLeave :dirty="v$.$anyDirty" />
    </div>
</template>
